import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Package, Store, Truck, Eye } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminFarmDrawer({ vendorId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!vendorId) return;
    setD(null);
    api.get(`/admin/vendors/${vendorId}/detail`).then(r => setD(r.data)).catch(() => setD(false));
  }, [vendorId]);
  if (!vendorId) return null;

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="admin-farm-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[var(--ff-bg,#fff)] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[var(--ff-border)] p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <p className="font-display text-xl">Farm backend</p>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full"><Eye size={10} /> Read-only</span>
          </div>
          <button onClick={onClose} data-testid="admin-farm-drawer-close" className="p-1 hover:bg-stone-100 rounded"><X size={18} /></button>
        </div>

        {d === null && <p className="p-8 text-sm text-[var(--ff-text-muted)]">Loading…</p>}
        {d === false && <p className="p-8 text-sm text-rose-700">Failed to load farm.</p>}
        {d && d.vendor && (
          <div className="p-5 space-y-6">
            <div className="flex items-center gap-4">
              {d.vendor.image_url ? <img src={d.vendor.image_url} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-stone-200" />}
              <div>
                <p className="font-display text-2xl">{d.vendor.farm_name}</p>
                <p className="text-sm text-[var(--ff-text-muted)]">{d.vendor.location} · {d.owner?.name || "—"} · {d.owner?.email || d.vendor.owner_email || "—"}</p>
                <p className="text-xs uppercase tracking-wider text-[var(--ff-primary)] mt-1">{d.vendor.vendor_status}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-farm-finance">
              {[["Gross sales", money(d.finance.gross)], ["Commission", money(d.finance.commission)], ["Payout pending", money(d.finance.payout_pending)], ["Payout paid", money(d.finance.payout_paid)]].map(([l, v]) => (
                <div key={l} className="card-flat p-3"><p className="text-[11px] uppercase tracking-wider text-[var(--ff-text-muted)]">{l}</p><p className="font-display text-xl mt-1">{v}</p></div>
              ))}
            </div>

            {d.vendor.story && <div><p className="overline mb-1 flex items-center gap-1"><Store size={12} /> Storefront story</p><p className="text-sm text-[var(--ff-text-secondary)]">{d.vendor.story}</p></div>}

            <div>
              <p className="overline mb-2 flex items-center gap-1"><Truck size={12} /> Shipping</p>
              <div className="card-flat p-3 text-sm">
                <p>Default rate: {money(d.vendor.default_shipping_rate ?? 0)} · Free over: {d.vendor.free_shipping_over ? money(d.vendor.free_shipping_over) : "—"}</p>
                {(d.vendor.shipping_zones || []).length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-[var(--ff-text-muted)]">
                    {d.vendor.shipping_zones.map((z, i) => <li key={i}>{z.name}: {(z.states || []).join(", ")} — {money(z.rate)}</li>)}
                  </ul>
                )}
              </div>
            </div>

            <div>
              <p className="overline mb-2 flex items-center gap-1"><Package size={12} /> Products ({d.finance.products_count})</p>
              <div className="card-flat divide-y divide-[var(--ff-border)]" data-testid="admin-farm-products">
                {d.products.length === 0 && <p className="p-4 text-sm text-[var(--ff-text-muted)]">No products.</p>}
                {d.products.map(p => (
                  <div key={p.id} className="p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0"><p className="text-sm font-medium truncate">{p.name}</p><p className="text-xs text-[var(--ff-text-muted)]">{p.category || "—"} · {p.approval_status || "approved"}{p.active === false ? " · inactive" : ""}</p></div>
                    <p className="text-sm font-semibold shrink-0">{money(p.price)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="overline mb-2">Orders ({d.finance.orders_count})</p>
              <div className="card-flat divide-y divide-[var(--ff-border)]" data-testid="admin-farm-orders">
                {d.suborders.length === 0 && <p className="p-4 text-sm text-[var(--ff-text-muted)]">No orders yet.</p>}
                {d.suborders.map(s => (
                  <div key={s.id} className="p-3 flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0"><p className="font-mono text-xs text-[var(--ff-text-muted)]">#{(s.order_id || "").slice(0, 10)}</p><p className="truncate">{(s.items || []).map(i => i.name).join(", ") || "—"}</p></div>
                    <div className="text-right shrink-0"><p className="font-semibold">{money(s.subtotal)}</p><p className="text-[11px] uppercase tracking-wider text-[var(--ff-primary)]">{s.status} · {s.payout_status || "pending"}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
